import { Receipt } from 'lucide-react';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { LuxuryTable, SectionTitle, GoldBadge } from '../Luxury';
import { useActiveMarket } from '../../store/useMarketStore';

export interface OpExLineItem {
  category: string;
  amount: number;
  formula?: string;
  ssotSource?: string;
}

interface OpExBreakdownProps {
  lineItems: OpExLineItem[];
  totalOpEx: number;
  grossIncome: number;
  ssotTierRate?: number;
  className?: string;
}

/**
 * OpExBreakdown component displays operating expense line items with SSOT formulas.
 *
 * Features:
 * - Line-by-line operating expense breakdown
 * - Categories: Property tax, Insurance, Utilities, Maintenance, PM fee, Vacancy, CapEx reserve
 * - Formula column shows SSOT calculation (e.g., "4.5% of ARV" for property tax)
 * - SSOT badge (GoldBadge variant='ssot') next to items with config.py source
 * - OpEx ratio percentage shown at bottom (OpEx / Gross Income x 100)
 * - Uses LuxuryTable for dark theme styling
 * - JetBrains Mono for all dollar amounts
 */
export function OpExBreakdown({
  lineItems,
  totalOpEx,
  grossIncome,
  ssotTierRate,
  className = '',
}: OpExBreakdownProps) {
  const market = useActiveMarket();
  // Calculate OpEx ratio with sanity check
  const opexRatio = grossIncome > 0 ? totalOpEx / grossIncome : 0;

  // Calculate percentage of EGI for each line item with sanity check
  const getPercentOfEGI = (amount: number) => {
    if (!grossIncome || grossIncome <= 0) return 0;
    const percent = amount / grossIncome;
    // Sanity check: no single line item should exceed 50% of EGI
    // If it does, likely data mapping error - show 0 instead of misleading value
    return percent > 0.5 ? 0 : percent;
  };

  return (
    <div className={`bg-obsidian rounded-xl p-6 ${className}`}>
      <SectionTitle icon={<Receipt className="w-5 h-5" />}>
        Operating Expense Breakdown
      </SectionTitle>

      <LuxuryTable>
        <thead>
          <tr>
            <th>Category</th>
            <th className="text-right">% of EGI</th>
            <th className="text-right">Annual</th>
            <th>Formula / Source</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const percentOfEGI = getPercentOfEGI(item.amount);

            return (
              <tr key={idx}>
                <td className="flex items-center gap-2">
                  <span>{item.category}</span>
                  {item.ssotSource && (
                    <GoldBadge variant="ssot" className="text-sm px-2 py-0.5">
                      SSOT
                    </GoldBadge>
                  )}
                </td>
                <td className="num text-stone-400">
                  {formatPercent(percentOfEGI, 1)}
                </td>
                <td className="num">
                  {formatCurrency(item.amount)}
                </td>
                <td className="text-sm text-stone-400">
                  {item.formula || (item.ssotSource ? `from ${item.ssotSource}` : '--')}
                </td>
              </tr>
            );
          })}

          {/* Total row */}
          <tr className="total-row">
            <td className="font-bold">Total Operating Expenses</td>
            <td className="num">
              {formatPercent(opexRatio, 1)}
            </td>
            <td className="num">
              {formatCurrency(totalOpEx)}
            </td>
            <td></td>
          </tr>
        </tbody>
      </LuxuryTable>

      {/* OpEx ratio summary */}
      <div className="mt-4 flex flex-wrap items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-stone-400">Effective OpEx Ratio:</span>
          <span className="font-mono tabular-nums font-medium text-ivory">
            {formatPercent(opexRatio, 1)}
          </span>
        </div>
        {ssotTierRate != null && ssotTierRate > 0 && (
          <>
            <span className="text-stone-400">|</span>
            <div className="flex items-center gap-2">
              <span className="text-stone-400">SSOT Tier Rate:</span>
              <span className="font-mono tabular-nums font-medium text-gold">
                {formatPercent(ssotTierRate, 1)}
              </span>
            </div>
          </>
        )}
        <span className="text-stone-400">|</span>
        <div className="text-[12px] text-stone-400">
          Total OpEx / Post-Reno EGI {ssotTierRate != null && ssotTierRate > 0 ? '(differs from tier rate when actual property tax is substituted)' : ''}
        </div>
      </div>

      {/* SSOT methodology note */}
      <div className="mt-4 p-3 bg-charcoal/50 rounded-lg border border-slate-dark">
        <div className="text-sm text-stone-400 leading-relaxed">
          <span className="text-emerald-400 font-medium">SSOT:</span>{' '}
          Operating expenses calculated using tier-based ratios from config.py.
          Property taxes sourced from {market.assessor_name} API when available.
        </div>
      </div>
    </div>
  );
}
